import API from './api';

/**
 * AI spend analytics (platform admin only).
 *
 * Thin wrappers, no try/catch: callers get the real axios error, matching the
 * convention documented in docs/en/services.md.
 *
 * Money arrives as JSON numbers in USD with field names ending `_usd`. There is
 * no cents field and no pre-formatted string anywhere in this contract, so the
 * UI must never divide, multiply or round a monetary value.
 */

const BASE = '/aispend';

export const getAiSpendFilterOptions = ({ params, signal } = {}) =>
  API.get(`${BASE}/filter_options/`, { params, signal }).then((r) => r.data);

export const getAiSpendOverview = ({ params, signal } = {}) =>
  API.get(`${BASE}/overview/`, { params, signal }).then((r) => r.data);

/** params adds bucket=day|week|month and group_by=none|provider|service|feature|model */
export const getAiSpendTimeseries = ({ params, signal } = {}) =>
  API.get(`${BASE}/timeseries/`, { params, signal }).then((r) => r.data);

export const getAiSpendHeatmap = ({ params, signal } = {}) =>
  API.get(`${BASE}/heatmap/`, { params, signal }).then((r) => r.data);

/** One row shape for provider / service / feature / model / organization. */
export const getAiSpendBreakdown = ({ dimension, params, signal } = {}) =>
  API.get(`${BASE}/breakdown/`, { params: { ...params, dimension }, signal }).then(
    (r) => r.data,
  );

export const getAiSpendUsers = ({ params, signal } = {}) =>
  API.get(`${BASE}/users/`, { params, signal }).then((r) => r.data);

export const getAiSpendUserDetail = ({ subjectUuid, params, signal } = {}) =>
  API.get(`${BASE}/users/${subjectUuid}/`, { params, signal }).then((r) => r.data);

export const getAiSpendSessions = ({ params, signal } = {}) =>
  API.get(`${BASE}/sessions/`, { params, signal }).then((r) => r.data);

export const getAiSpendErrors = ({ params, signal } = {}) =>
  API.get(`${BASE}/errors/`, { params, signal }).then((r) => r.data);

export const getAiSpendRuns = ({ params, signal } = {}) =>
  API.get(`${BASE}/runs/`, { params, signal }).then((r) => r.data);

export const getAiSpendRun = ({ runUuid, signal } = {}) =>
  API.get(`${BASE}/runs/${runUuid}/`, { signal }).then((r) => r.data);

export const getAiSpendDaily = ({ params, signal } = {}) =>
  API.get(`${BASE}/daily/`, { params, signal }).then((r) => r.data);

export const getAiSpendHealth = ({ signal } = {}) =>
  API.get(`${BASE}/health/`, { signal }).then((r) => r.data);

/**
 * CSV export through the axios instance, so the bearer token is attached and a
 * mid-download token refresh still yields a blob (the retry reuses the original
 * config, `responseType` included).
 */
export const exportAiSpendRuns = ({ params, signal } = {}) =>
  API.get(`${BASE}/runs/export.csv`, { params, signal, responseType: 'blob' }).then(
    (response) => ({
      blob: response.data,
      filename:
        String(response.headers?.['content-disposition'] || '').match(
          /filename="?([^";]+)"?/i,
        )?.[1] || 'ai-spend-runs.csv',
    }),
  );
